import { createStore } from 'zustand/vanilla';
import PlacementOrder from '@/models/PlacementOrder';
import Address from '@/models/Address';
import Airport from '@/models/Airport';
import Flight from '@/models/Flight';
import VehicleType from '@/models/VehicleType';
import Addon from '@/models/Addon';
import Passenger from '@/models/Passenger';
import LuggageCounter from '@/models/LuggageCounter';
import Coupons from '@/models/Coupons';

/**
 * @typedef {Object} GlobalState - Represents the state of the global store.
 * @property {PlacementOrder} placementOrder - The current placement order.
 * @property {Coupons|null} coupons - The current coupons.
 */

/**
 * @typedef {Object} GlobalActions - Represents the actions available for the global store.
 * @property {(orderType: string) => void} setOrderType - Sets the order type of the placement order.
 * @property {(address: Address) => void} setPickUpOrDropOffLocation - Sets the pick-up location of the placement order.
 * @property {(airport: Airport) => void} setAirportLocation - Sets the airport location of the placement order.
 * @property {(time: Date) => void} setPickUpTime - Sets the pick-up time of the placement order.
 * @property {(flight: Flight) => void} setFlightInformation - Sets the flight information of the placement order.
 * @property {(rawFlightDate: string) => void} setRawFlightDate - Sets the raw flight date of the placement order.
 * @property {(rawFlightNumber: string) => void} setRawFlightNumber - Sets the raw flight number of the placement order.
 * @property {(count: number) => void} setPassengerCount - Sets the passenger count of the placement order.
 * @property {(counter: LuggageCounter) => void} setLuggageCounter - Sets the luggage count of the placement order.
 * @property {(vehicle: VehicleType) => void} setVehicleType - Sets the vehicle type of the placement order.
 * @property {(description: string) => void} setDescription - Sets the description of the placement order.
 * @property {(addon: Addon) => void} setAddon - Sets the addon of the placement order.
 * @property {(passenger: Passenger) => void} setPassenger - Sets the passenger of the placement order.
 * @property {(extraStops: Array<Address>) => void} setExtraStops - Sets the extra stops of the placement order.
 * @property {(remoteAreaSurchargeId: string) => void} setRemoteAreaSurchargeId - Sets the remote area surcharge ID of the placement order.
 * @property {() => void} reset - Resets the placement order to the initial state.
 * @property {(placementOrder: PlacementOrder) => void} setOrder - Sets the placement order.
 * @property {(couponId: string) => void} setCouponId - Sets the coupon ID of the placement order.
 *
 * @property {(coupons: Coupons | null) => void} setCoupons
 */

/**
 *
 * @typedef {GlobalState & GlobalActions} GlobalStore - Represents the global store, combining the state and actions.
 */
export const Types = {};

/**
 * Initializes the global store.
 * @returns {GlobalState} The initial state of the global store.
 */
export const initGlobalStore = () => {
  return {
    placementOrder: new PlacementOrder(),
    coupons: null,
  };
};

/**
 * Initializes the global store.
 * @type {GlobalState}
 */
export const defaultInitState = {
  placementOrder: new PlacementOrder(),
  coupons: null,
};

/**
 * Creates a global store with the given initial state.
 * @param {GlobalState} initState - The initial state for the global store.
 * @returns {GlobalStore} The created global store.
 */
export const createGlobalStore = (initState = defaultInitState) => {
  return createStore()((set) => ({
    ...initState,
    /**
     * 訂單相關
     */
    setOrderType: (orderType) =>
      set((state) => ({ ...state, placementOrder: state.placementOrder.setOrderType(orderType) })),
    setPickUpOrDropOffLocation: (address) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setPickUpOrDropOffLocation(address),
      })),
    setAirportLocation: (airport) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setAirportLocation(airport),
      })),
    setPickUpTime: (time) =>
      set((state) => ({ ...state, placementOrder: state.placementOrder.setPickUpTime(time) })),
    setFlightInformation: (flight) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setFlightInformation(flight),
      })),
    setRawFlightDate: (rawFlightDate) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setRawFlightDate(rawFlightDate),
      })),
    setRawFlightNumber: (rawFlightNumber) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setRawFlightNumber(rawFlightNumber),
      })),
    setPassengerCount: (count) =>
      set((state) => ({ ...state, placementOrder: state.placementOrder.setPassengerCount(count) })),
    setLuggageCounter: (counter) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setLuggageCounter(counter),
      })),
    setVehicleType: (vehicle) =>
      set((state) => ({ ...state, placementOrder: state.placementOrder.setVehicleType(vehicle) })),
    setDescription: (description) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setDescription(description),
      })),
    setAddon: (addon) =>
      set((state) => ({ ...state, placementOrder: state.placementOrder.setAddon(addon) })),
    setNameSignContent: (nameSignContent) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setNameSignContent(nameSignContent),
      })),
    setPassenger: (passenger) =>
      set((state) => ({ ...state, placementOrder: state.placementOrder.setPassenger(passenger) })),
    setExtraStops: (extraStops) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setExtraStops(extraStops),
      })),
    setIsPriceTableLoading: (nextIsPriceTableLoading) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setIsPriceTableLoading(nextIsPriceTableLoading),
      })),
    setIsExtraStopsValid: (isValid) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setIsExtraStopsValid(isValid),
      })),
    setRemoteAreaSurchargeId: (remoteAreaSurchargeId) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setRemoteAreaSurchargeId(remoteAreaSurchargeId),
      })),
    reset: () => set((state) => ({ ...state, placementOrder: new PlacementOrder() })),
    setOrder: (placementOrder) => set((state) => ({ ...state, placementOrder })),
    setCouponId: (couponId) =>
      set((state) => ({ ...state, placementOrder: state.placementOrder.setCouponId(couponId) })),
    /**
     * 優惠券相關
     */
    setCoupons: (coupons) => set((state) => ({ ...state, coupons })),
  }));
};
